import * as React from 'react';

import type {
  State,
  Dialog,
  DialogComponent,
  DialogResponse,
  Subscriber,
} from './types';

type StackType = Array<Dialog<any>>;

// we wrap the state inside a function so that we can create a blank
// state for each unit test. we do not expose this init function to
// package consumers; instead we call it in the index.js and export
// the API it returns
export function init() {
  // to support displaying a new dialog while one is already visible, we
  // store the open dialogs as a stack. when a dialog is already open,
  // we push any new dialogs on top and show them instead, then
  // return to the previous dialog after closing the new one.
  const stack: StackType = [];

  // we store the current state so that any new components can
  // get the latest state as their internal state
  let currentState: State<any> = null;

  // we'll notify the rest of the app about state updates via a
  // subscription pattern, with callbacks stored in this set
  const subscribers = new Set<Subscriber<any>>();

  // any time we want to trigger a state update, we grab the current
  // dialog (default to null if empty), update the stored state,
  // and then iterate over the subscribers and call each one
  // with the new state
  function triggerUpdate() {
    currentState = stack[stack.length - 1] || null;
    subscribers.forEach((callback: Subscriber<any>) => callback(currentState));
  }

  // we expose this state to the dialog system tests via a `subscribe`
  // function which adds a single subscriber to the set
  function subscribe(callback: Subscriber<any>) {
    subscribers.add(callback);

    // we return a cleanup function so that subscribers can
    // remove the subscription when they're done
    return () => {
      subscribers.delete(callback);
    };
  }

  // we expose the state to the dialog system components via a hook
  // which lets us easily subscribe to updates in a react component
  function useDialogState<T = void>(): State<T> {
    const [current, setState] = React.useState<State<T>>(currentState);

    React.useEffect(
      () => {
        const subscriberCallback = (state: State<T>) => {
          setState(state);
        };

        const cleanup = subscribe(subscriberCallback);
        if (currentState !== current) {
          setState(currentState);
        }
        // we only want to re-run this effect when setState changes, but
        // we need to check whether state has changed in case a dialog
        // has been triggered in between the state initialising and
        // this effect firing.
        return cleanup;
      },
      // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [setState]
    );
    return current;
  }

  // when we want to close the open dialog, we pop it off
  // the stack and trigger an update
  function close() {
    stack.pop();
    triggerUpdate();
  }

  // when we want to open a dialog, we push it on top of
  // the stack and trigger an update
  const showDialog = <T = void>(
    Component: DialogComponent<T>
  ): Promise<DialogResponse<T>> =>
    new Promise((resolve) => {
      stack.push({
        Component,
        resolve: (res: DialogResponse<T>) => {
          resolve(res);
          close();
        },
      });
      triggerUpdate();
    });

  return {
    showDialog,
    subscribe,
    useDialogState,
  };
}

export const {showDialog, useDialogState} = init();
